/**
 * Auto-generated by scripts/generate-drizzle-schema.mjs
 * Regenerate with: make generate-schema
 */

import { DrizzleAppSchema, type PowerSyncSQLiteDatabase } from "@powersync/drizzle-driver";
import { index, integer, real, sqliteTable, text } from "drizzle-orm/sqlite-core";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";

// ACTIVITY-CORE (feed del hogar): espejo acotado que sincroniza PowerSync.
export const activity_recent = sqliteTable(
  "activity_recent",
  {
    id: text("id").primaryKey(),
    scope_id: text("scope_id"),
    list_id: text("list_id"),
    entity_type: text("entity_type"),
    entity_id: text("entity_id"),
    action: text("action"),
    actor_user_id: text("actor_user_id"),
    actor_name: text("actor_name"),
    actor_device_id: text("actor_device_id"),
    platform: text("platform"),
    server_ts: text("server_ts"),
    client_ts: text("client_ts"),
    patch: text("patch"),
    batch_id: text("batch_id"),
    reverts_activity_id: text("reverts_activity_id"),
  },
  (table) => [
    /** ORDER BY server_ts DESC en el feed de actividad */
    index("activity_recent_by_feed").on(table.server_ts),
    /** WHERE entity_type = ? AND entity_id = ? en el historial por entidad */
    index("activity_recent_by_entity").on(table.entity_type, table.entity_id, table.server_ts),
  ],
);

export const aisle = sqliteTable(
  "aisle",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    name: text("name"),
    sort_order: integer("sort_order"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? ORDER BY sort_order en useAislesQuery() */
    index("aisle_by_user").on(table.user_id, table.sort_order),
  ],
);

export const attachment = sqliteTable(
  "attachment",
  {
    id: text("id").primaryKey(),
    filename: text("filename"),
    content_type: text("content_type"),
    size: integer("size"),
    url: text("url"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    user_id: text("user_id"),
    is_shared: integer("is_shared"),
    hash: text("hash"),
  },
  (table) => [
    /** WHERE user_id = ? en getHasLocalDataQuery */
    index("attachment_by_user").on(table.user_id),
  ],
);

export const font_family = sqliteTable("font_family", {
  id: text("id").primaryKey(),
  family: text("family"),
  name: text("name"),
  category: text("category"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
});

export const font_variant = sqliteTable("font_variant", {
  id: text("id").primaryKey(),
  font_family_id: text("font_family_id"),
  weight: text("weight"),
  url: text("url"),
  created_at: text("created_at"),
  attachment_id: text("attachment_id"),
});

export const meal_plan_entry = sqliteTable(
  "meal_plan_entry",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    plan_date: text("plan_date"),
    meal_type: text("meal_type"),
    recipe_id: text("recipe_id"),
    servings: real("servings"),
    note: text("note"),
    // 🔖 SELLO puntero/sello (00051): versión de la receta con la que se
    // planificó este plato (inmutable). NULL en platos legacy. El badge
    // "receta modificada desde que se planificó" es recipe_version_id != NULL
    // AND recipe_version_id != recipe.current_version_id.
    recipe_version_id: text("recipe_version_id"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? AND plan_date BETWEEN ? AND ? en useMealPlanWeekQuery() */
    index("meal_plan_entry_by_user_date").on(table.user_id, table.plan_date),
  ],
);

// Dataset pequeño por usuario — índices no necesarios por ahora.
export const member = sqliteTable("member", {
  id: text("id").primaryKey(),
  organization_id: text("organization_id"),
  user_id: text("user_id"),
  role: text("role"),
  created_at: text("created_at"),
});

// Dataset pequeño — índices no necesarios por ahora.
export const organization = sqliteTable("organization", {
  id: text("id").primaryKey(),
  name: text("name"),
  slug: text("slug"),
  logo: text("logo"),
  metadata: text("metadata"),
  created_at: text("created_at"),
});

export const pantry_item = sqliteTable(
  "pantry_item",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    name: text("name"),
    quantity: real("quantity"),
    unit: text("unit"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? en usePantryQuery() y generación de lista */
    index("pantry_item_by_user").on(table.user_id),
  ],
);

export const recipe = sqliteTable(
  "recipe",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    name: text("name"),
    photo_url: text("photo_url"),
    servings: integer("servings"),
    steps: text("steps"),
    tags: text("tags"),
    notes: text("notes"),
    // 🔖 Puntero puntero/sello (00051): versión current de la receta (== su
    // contenido VIVO cuando es NON-NULL; NULL tras editar por la unificación
    // "current-becomes-history"). El badge del planner compara el sello del
    // plato contra este puntero.
    current_version_id: text("current_version_id"),
    // Papelera L2 (soft-delete): NULL = viva; las vistas filtran deleted_at IS NULL.
    deleted_at: text("deleted_at"),
    // 🥗 Nutrición POR PORCIÓN (opcional, 00052). El planner escala por las
    // porciones planificadas del plato. NO versionada (L4) ni trazada (L3):
    // restaurar una versión conserva la nutrición actual. Ver docs/NUTRITION.md.
    calories: real("calories"),
    protein_g: real("protein_g"),
    carbs_g: real("carbs_g"),
    fat_g: real("fat_g"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? ORDER BY updated_at DESC en useRecipesQuery() */
    index("recipe_by_user").on(table.user_id, table.updated_at),
  ],
);

export const recipe_ingredient = sqliteTable(
  "recipe_ingredient",
  {
    id: text("id").primaryKey(),
    recipe_id: text("recipe_id"),
    user_id: text("user_id"),
    name: text("name"),
    quantity: real("quantity"),
    unit: text("unit"),
    aisle_id: text("aisle_id"),
    sort_order: integer("sort_order"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE recipe_id = ? ORDER BY sort_order en useRecipeIngredientsQuery() */
    index("recipe_ingredient_by_recipe").on(table.recipe_id, table.sort_order),
    /** WHERE user_id = ? en la generación de lista de compra */
    index("recipe_ingredient_by_user").on(table.user_id),
  ],
);

// 📜 Historial de versiones de recetas (L4) — bucket acotado: solo las últimas
// 10 por receta llegan con in_sync_window=true (el historial profundo va por
// API paginada). Snapshot COMPUESTO: campos del padre + `ingredients` (JSON
// serializado) + `text` plano para el diff por palabra.
export const recipe_version = sqliteTable(
  "recipe_version",
  {
    id: text("id").primaryKey(),
    recipe_id: text("recipe_id"),
    user_id: text("user_id"),
    name: text("name"),
    servings: integer("servings"),
    steps: text("steps"),
    tags: text("tags"),
    notes: text("notes"),
    photo_url: text("photo_url"),
    ingredients: text("ingredients"),
    text: text("text"),
    client_ts: text("client_ts"),
    server_ts: text("server_ts"),
  },
  (table) => [
    /** WHERE recipe_id = ? ORDER BY id DESC en getRecipeVersionsQuery */
    index("recipe_version_by_recipe").on(table.recipe_id, table.id),
  ],
);

// Sesiones activas del usuario — dataset pequeño, sin índices.
export const session = sqliteTable("session", {
  id: text("id").primaryKey(),
  expires_at: text("expires_at"),
  token: text("token"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  ip_address: text("ip_address"),
  user_agent: text("user_agent"),
  user_id: text("user_id"),
  active_organization_id: text("active_organization_id"),
});

export const shopping_item = sqliteTable(
  "shopping_item",
  {
    id: text("id").primaryKey(),
    shopping_list_id: text("shopping_list_id"),
    user_id: text("user_id"),
    name: text("name"),
    quantity: real("quantity"),
    unit: text("unit"),
    category: text("category"),
    checked: integer("checked"),
    is_manual: integer("is_manual"),
    sort_order: integer("sort_order"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE shopping_list_id = ? ORDER BY checked, category, sort_order */
    index("shopping_item_by_list").on(
      table.shopping_list_id,
      table.checked,
      table.category,
      table.sort_order,
    ),
    /** WHERE user_id = ? en getHasLocalDataQuery */
    index("shopping_item_by_user").on(table.user_id),
  ],
);

export const shopping_list = sqliteTable(
  "shopping_list",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    name: text("name"),
    week_start: text("week_start"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? en useShoppingListsQuery() */
    index("shopping_list_by_user").on(table.user_id, table.updated_at),
  ],
);

export const shopping_list_organization = sqliteTable("shopping_list_organization", {
  id: text("id").primaryKey(),
  shopping_list_id: text("shopping_list_id"),
  organization_id: text("organization_id"),
  added_at: text("added_at"),
  added_by: text("added_by"),
});

// Dataset global pequeño (~20 temas) — índices no necesarios.
export const themes = sqliteTable("themes", {
  id: text("id").primaryKey(),
  name: text("name"),
  background: text("background"),
  surface: text("surface"),
  accent: text("accent"),
  glow_colors: text("glow_colors"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  is_dark: integer("is_dark"),
  foreground: text("foreground"),
  field_background: text("field_background"),
  danger: text("danger"),
  success: text("success"),
  warning: text("warning"),
  muted: text("muted"),
  pattern_type: text("pattern_type"),
  pattern_opacity: integer("pattern_opacity"),
  pair_id: text("pair_id"),
  font_normal: text("font_normal"),
  font_medium: text("font_medium"),
  font_semibold: text("font_semibold"),
  font_bold: text("font_bold"),
  radius: text("radius"),
  surface_foreground: text("surface_foreground"),
  surface_secondary: text("surface_secondary"),
  surface_secondary_foreground: text("surface_secondary_foreground"),
  surface_tertiary: text("surface_tertiary"),
  surface_tertiary_foreground: text("surface_tertiary_foreground"),
  overlay: text("overlay"),
  overlay_foreground: text("overlay_foreground"),
  default: text("default"),
  default_foreground: text("default_foreground"),
  accent_foreground: text("accent_foreground"),
  field_foreground: text("field_foreground"),
  field_placeholder: text("field_placeholder"),
  field_border: text("field_border"),
  danger_foreground: text("danger_foreground"),
  success_foreground: text("success_foreground"),
  warning_foreground: text("warning_foreground"),
  segment: text("segment"),
  segment_foreground: text("segment_foreground"),
  border: text("border"),
  focus: text("focus"),
  link: text("link"),
  surface_shadow: text("surface_shadow"),
  overlay_shadow: text("overlay_shadow"),
  field_shadow: text("field_shadow"),
  muted_foreground: text("muted_foreground"),
  divider: text("divider"),
  glass_background: text("glass_background"),
  glass_border: text("glass_border"),
  glass_highlight: text("glass_highlight"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
});

// Solo 1 fila por usuario — índices no necesarios.
export const user = sqliteTable("user", {
  id: text("id").primaryKey(),
  name: text("name"),
  email: text("email"),
  email_verified: integer("email_verified"),
  image: text("image"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  locale: text("locale"),
  global_theme_id: text("global_theme_id"),
  is_blur: integer("is_blur"),
  font_family: text("font_family"),
  follow_device_theme: integer("follow_device_theme"),
  role: text("role"),
  banned: integer("banned"),
  ban_reason: text("ban_reason"),
  ban_expires: text("ban_expires"),
  two_factor_enabled: integer("two_factor_enabled"),
  two_factor_secret: text("two_factor_secret"),
  two_factor_backup_codes: text("two_factor_backup_codes"),
  deletion_requested_at: text("deletion_requested_at"),
});

export const drizzleSchema = {
  activity_recent,
  aisle,
  attachment,
  font_family,
  font_variant,
  meal_plan_entry,
  member,
  organization,
  pantry_item,
  recipe,
  recipe_ingredient,
  recipe_version,
  session,
  shopping_item,
  shopping_list,
  shopping_list_organization,
  themes,
  user,
};

export const AppSchema = new DrizzleAppSchema(drizzleSchema);

export type Database = (typeof AppSchema)["types"];

/**
 * Type helper for the Drizzle database instance wrapped with PowerSync.
 */
export type PowerSyncDrizzleDB = PowerSyncSQLiteDatabase<typeof drizzleSchema>;

export const USER_DATA_TABLES = [
  "aisle",
  "attachment",
  "meal_plan_entry",
  "pantry_item",
  "recipe",
  "recipe_ingredient",
  "shopping_item",
  "shopping_list",
] as const;

/**
 * Tablas que viajan en el traspaso P2P dispositivo-a-dispositivo (subconjunto
 * CLIENTE-escribible de `USER_DATA_TABLES`; el orden es de INSERCIÓN: padres
 * antes que hijos → `aisle` y `recipe` antes que la tabla-hija
 * `recipe_ingredient`; `shopping_list` antes que `shopping_item`).
 *
 * ⚠️ CC-33: NO incluye `recipe_version`. El historial de versiones de la receta
 * es server-materializado (lo sella el write-path de la API) y de SOLO LECTURA
 * en el cliente — llega por el stream de sync. El server lo re-deriva al recibir
 * las recetas importadas; transferirlo generaría filas locales que el cliente no
 * puede subir. `recipe_version` tampoco vive en `USER_DATA_TABLES` (es
 * server-materializada), así que aquí el traspaso y el sweep coinciden en
 * excluirlo. (`shopping_list_organization` es una tabla de compartición de org,
 * tampoco en `USER_DATA_TABLES` → tampoco se transfiere.)
 *
 * Nota: los VALORES de columnas internas `*_version_id` (p. ej.
 * `meal_plan_entry.recipe_version_id`) viajan intactos dentro de las filas —
 * eso es correcto; solo se excluyen las FILAS de la TABLA `recipe_version`.
 */
export const P2P_TRANSFER_TABLES = [
  "aisle",
  "recipe",
  "recipe_ingredient",
  "meal_plan_entry",
  "pantry_item",
  "shopping_list",
  "shopping_item",
  "attachment",
] as const;

export type ActivityRecent = InferSelectModel<typeof activity_recent>;
export type ActivityRecentInsert = InferInsertModel<typeof activity_recent>;
export type ActivityRecentRecord = ActivityRecent;

export type Aisle = InferSelectModel<typeof aisle>;
export type AisleInsert = InferInsertModel<typeof aisle>;
export type AisleRecord = Aisle;
export type Attachment = InferSelectModel<typeof attachment>;
export type AttachmentInsert = InferInsertModel<typeof attachment>;
export type AttachmentRecord = Attachment;
export type FontFamily = InferSelectModel<typeof font_family>;
export type FontFamilyInsert = InferInsertModel<typeof font_family>;
export type FontFamilyRecord = FontFamily;
export type FontVariant = InferSelectModel<typeof font_variant>;
export type FontVariantInsert = InferInsertModel<typeof font_variant>;
export type FontVariantRecord = FontVariant;
export type MealPlanEntry = InferSelectModel<typeof meal_plan_entry>;
export type MealPlanEntryInsert = InferInsertModel<typeof meal_plan_entry>;
export type MealPlanEntryRecord = MealPlanEntry;
export type Member = InferSelectModel<typeof member>;
export type MemberInsert = InferInsertModel<typeof member>;
export type MemberRecord = Member;
export type Organization = InferSelectModel<typeof organization>;
export type OrganizationInsert = InferInsertModel<typeof organization>;
export type OrganizationRecord = Organization;
export type PantryItem = InferSelectModel<typeof pantry_item>;
export type PantryItemInsert = InferInsertModel<typeof pantry_item>;
export type PantryItemRecord = PantryItem;
export type Recipe = InferSelectModel<typeof recipe>;
export type RecipeInsert = InferInsertModel<typeof recipe>;
export type RecipeRecord = Recipe;
export type RecipeIngredient = InferSelectModel<typeof recipe_ingredient>;
export type RecipeIngredientInsert = InferInsertModel<typeof recipe_ingredient>;
export type RecipeIngredientRecord = RecipeIngredient;
export type RecipeVersion = InferSelectModel<typeof recipe_version>;
export type RecipeVersionInsert = InferInsertModel<typeof recipe_version>;
export type RecipeVersionRecord = RecipeVersion;
export type Session = InferSelectModel<typeof session>;
export type SessionInsert = InferInsertModel<typeof session>;
export type SessionRecord = Session;
export type ShoppingItem = InferSelectModel<typeof shopping_item>;
export type ShoppingItemInsert = InferInsertModel<typeof shopping_item>;
export type ShoppingItemRecord = ShoppingItem;
export type ShoppingList = InferSelectModel<typeof shopping_list>;
export type ShoppingListInsert = InferInsertModel<typeof shopping_list>;
export type ShoppingListRecord = ShoppingList;
export type ShoppingListOrganization = InferSelectModel<typeof shopping_list_organization>;
export type ShoppingListOrganizationInsert = InferInsertModel<typeof shopping_list_organization>;
export type ShoppingListOrganizationRecord = ShoppingListOrganization;
export type Themes = InferSelectModel<typeof themes>;
export type ThemesInsert = InferInsertModel<typeof themes>;
export type ThemesRecord = Themes;
export type User = InferSelectModel<typeof user>;
export type UserInsert = InferInsertModel<typeof user>;
export type UserRecord = User;
